import gsap from 'gsap';
import { ref, watch } from 'vue';

import { useFocusManagement } from './use-focus-management';

/**
 * @typedef {'keyboard' | 'hover' | 'other'} ActivationMethod
 */

/**
 * @callback PrepareActivationFn
 * @param {ActivationMethod} method - The method used to activate the menu ('keyboard' or 'hover').
 * @returns {void}
 */

/**
 * @typedef {Object} HeaderDesktopFocusOptions
 * @property {import('vue').Ref<boolean>} isDesktopMenuOpen - Reactive ref indicating if any desktop menu is open.
 * @property {import('vue').Ref<string | null>} activeDesktopMenu - Reactive ref indicating the ID of the currently active menu.
 * @property {() => HTMLElement | null} getLastTriggerElement - Function that returns the DOM element which last triggered a menu open.
 * @property {(menuId: string) => HTMLElement | null} getPanelElement - Function that returns the DOM element for a given menu panel ID.
 * @property {(categoryId: string) => void} setActiveCategory - Function to set the active category state.
 * @property {PrepareActivationFn} prepareActivation - Function to prepare activation based on interaction method.
 */

/**
 * Manages focus trapping and restoration specifically for the desktop header mega menu panels.
 * It orchestrates useFocusManagement based on the state provided by useDesktopMenu.
 *
 * @param {Omit<HeaderDesktopFocusOptions, 'prepareActivation'>} options - Options excluding prepareActivation as it's defined internally.
 * @returns {{ prepareActivation: PrepareActivationFn }} - Exposes the prepareActivation function.
 */
export function useHeaderDesktopFocus(options) {
  const {
    activeDesktopMenu,
    getLastTriggerElement,
    getPanelElement,
    isDesktopMenuOpen,
    setActiveCategory,
  } = options;

  const activePanelRef = ref(null);
  const focusTriggerElementRef = ref(null); // The button that opened the current panel
  const isPanelFocusActive = ref(false); // Controls the activation of focus management
  const shouldFocusFirst = ref(true); // Controls if first element gets focus
  const shouldRestoreFocus = ref(true); // Controls if focus should be restored on deactivation
  const lastActivationMethod = ref('keyboard'); // Track how the menu was last activated

  /**
   * Sets whether the focus management should focus the first element upon activation.
   * @type {PrepareActivationFn}
   */
  const prepareActivation = (method) => {
    lastActivationMethod.value = method;
    // Prevent focus ring and focus restoration on desktop menu hover
    shouldFocusFirst.value = method !== 'hover';
    shouldRestoreFocus.value = method !== 'hover';
  };

  // --- Specific Activation Handler for Header ---
  /**
   * Handles Enter/Space keydown events delegated by useFocusManagement.
   * Checks if the target is a secondary navigation button and calls setActiveCategory.
   * @param {KeyboardEvent} event
   */
  const handleElementActivation = (event) => {
    const targetElement = event.target;
    // Check if activating a secondary nav button
    if (
      targetElement instanceof HTMLElement &&
      targetElement.matches('[data-testid^="desktop-secondary-nav-button-"]')
    ) {
      event.preventDefault();
      const testId = targetElement.dataset.testid;
      const buttonPrefix = 'desktop-secondary-nav-button-';
      const categoryId = testId?.replace(buttonPrefix, '');
      if (categoryId) {
        setActiveCategory(categoryId);
      }
    }
  };

  useFocusManagement({
    containerRef: activePanelRef,
    focusFirstOnActivate: shouldFocusFirst,
    focusTriggerElementRef: focusTriggerElementRef,
    isActive: isPanelFocusActive,
    onElementActivated: handleElementActivation,
    shouldRestoreFocus,
    trapFocus: true,
  });

  // Watch for changes in the active menu or its open state
  watch(
    [isDesktopMenuOpen, activeDesktopMenu],
    ([newIsOpen, newMenuId], [oldIsOpen]) => {
      if (newIsOpen && newMenuId) {
        // --- Menu Opened or Switched ---
        const panelElement = getPanelElement(newMenuId);
        const triggerElement = getLastTriggerElement();

        activePanelRef.value = panelElement;
        focusTriggerElementRef.value = triggerElement;

        // Activate focus management AFTER setting up refs
        // The decision to focus first has already been made by prepareActivation
        isPanelFocusActive.value = true;
      } else if (!newIsOpen && oldIsOpen) {
        // --- Menu Closed ---
        isPanelFocusActive.value = false;
        // Focus restoration is handled by useFocusManagement via the isActive watcher
        // Clear refs after focus management has potentially restored focus
        gsap.delayedCall(0, () => {
          activePanelRef.value = null;
          focusTriggerElementRef.value = null;
        });
      }
    },
    { flush: 'post' }, // Ensure DOM updates before focus logic
  );

  return { prepareActivation };
}
